import type {
  CreateFeatureFlagRequest,
  FeatureFlagAssignmentRolloutTarget,
  FeatureFlagApiClientApplication,
  FeatureFlagClientConfiguration,
  FeatureFlagMetadataFormValues,
  FeatureFlagPayloadRolloutScope,
  FeatureFlagPayloadRolloutTarget,
  FeatureFlagTargetClient,
  ReplaceFeatureFlagStoresRequest,
  ReplaceFeatureFlagUsersRequest,
  UpdateFeatureFlagRequest,
} from '../../../types/features/featureFlags';

export const mapTargetClientToApiClientApplication = (
  targetClient: FeatureFlagTargetClient,
): FeatureFlagApiClientApplication => (targetClient === 'mobile_app' ? 'react_native' : 'webapp');

const getFeatureFlagPayloadRolloutScope = (
  configuration: FeatureFlagClientConfiguration,
): FeatureFlagPayloadRolloutScope => {
  if (configuration.globalEnabled) {
    return 'all';
  }

  return 'selected';
};

const getFeatureFlagRolloutTarget = (
  configuration: FeatureFlagClientConfiguration,
): FeatureFlagAssignmentRolloutTarget => (
  configuration.assignmentTarget === 'user_wise' ? 'users' : 'stores'
);

const getFeatureFlagPayloadRolloutTarget = (
  configuration: FeatureFlagClientConfiguration,
): FeatureFlagPayloadRolloutTarget => {
  if (configuration.globalEnabled) {
    return 'all';
  }

  return getFeatureFlagRolloutTarget(configuration);
};

export const buildCreateFeatureFlagRequest = (
  metadataValues: FeatureFlagMetadataFormValues,
  targetClient: FeatureFlagTargetClient,
  configuration: FeatureFlagClientConfiguration,
): CreateFeatureFlagRequest => ({
  client_application: mapTargetClientToApiClientApplication(targetClient),
  confirm_all: getFeatureFlagPayloadRolloutScope(configuration) === 'all',
  description: metadataValues.description.trim(),
  display_name: metadataValues.displayName.trim(),
  global_enabled: configuration.globalEnabled,
  key: metadataValues.featureKey.trim(),
  rollout_scope: getFeatureFlagPayloadRolloutScope(configuration),
  rollout_target: getFeatureFlagPayloadRolloutTarget(configuration),
});

export const buildReplaceFeatureFlagStoresRequest = (
  storeIds: string[],
): ReplaceFeatureFlagStoresRequest => ({
  store_ids: storeIds,
});

export const buildReplaceFeatureFlagUsersRequest = (
  userIds: string[],
): ReplaceFeatureFlagUsersRequest => ({
  user_ids: userIds,
});

export const buildUpdateFeatureFlagRequest = (
  metadataValues: FeatureFlagMetadataFormValues,
  configuration: FeatureFlagClientConfiguration,
): UpdateFeatureFlagRequest => ({
  confirm_all: getFeatureFlagPayloadRolloutScope(configuration) === 'all',
  description: metadataValues.description.trim(),
  display_name: metadataValues.displayName.trim(),
  global_enabled: configuration.globalEnabled,
  rollout_scope: getFeatureFlagPayloadRolloutScope(configuration),
  rollout_target: getFeatureFlagPayloadRolloutTarget(configuration),
});

export const shouldReplaceStoresAfterFeatureUpdate = (
  originalGlobalEnabled: boolean,
  originalRolloutTarget: FeatureFlagPayloadRolloutTarget,
  nextConfiguration: FeatureFlagClientConfiguration,
  storeAssignmentsChanged: boolean,
): boolean => (
  !nextConfiguration.globalEnabled
  && nextConfiguration.assignmentTarget === 'store_wise'
  && (storeAssignmentsChanged || originalGlobalEnabled || originalRolloutTarget !== 'stores')
);

export const shouldReplaceUsersAfterFeatureUpdate = (
  originalGlobalEnabled: boolean,
  originalRolloutTarget: FeatureFlagPayloadRolloutTarget,
  nextConfiguration: FeatureFlagClientConfiguration,
  userAssignmentsChanged: boolean,
): boolean => (
  !nextConfiguration.globalEnabled
  && nextConfiguration.assignmentTarget === 'user_wise'
  && (userAssignmentsChanged || originalGlobalEnabled || originalRolloutTarget !== 'users')
);
